<template>
  <div class="tab-content">
    <div
      class="log-toolbar"
      v-if="!isLoadingNotificationLog && normalizedLogs.length > 0"
    >
      <div class="log-toolbar-title">
        {{
          $t('notifications_modal.items_count', {
            filtered: filteredLogs.length,
            total: normalizedLogs.length,
          })
        }}
      </div>
      <div class="log-toolbar-hint">
        {{ $t('notifications_modal.notification_history') }}
      </div>
    </div>

    <div
      class="log-search"
      v-if="!isLoadingNotificationLog && normalizedLogs.length > 0"
    >
      <b-input
        v-model="searchQuery"
        icon="magnify"
        type="search"
        :placeholder="$t('notifications_modal.search_notifications')"
      />
    </div>

    <div class="notification-log-box">
      <div v-if="isLoadingNotificationLog" class="empty-state">
        {{ $t('notifications_modal.loading_log') }}
      </div>

      <div v-else-if="normalizedLogs.length === 0" class="empty-state">
        {{ $t('notifications_modal.no_notification_log') }}
      </div>

      <div v-else-if="filteredLogs.length === 0" class="empty-state">
        {{ $t('notifications_modal.no_matching_notifications') }}
      </div>

      <div v-else class="notification-log-items">
        <div
          v-for="log in filteredLogs"
          :key="log._id || log.sentAt + '-' + log.providerMessageId"
          class="notification-log-item"
        >
          <div class="log-card-header">
            <div class="log-card-title-row">
              <span class="log-card-title">{{ formatLogTitle(log) }}</span>
              <span class="log-meta-pill log-meta-pill-sent">
                <strong>{{ $t('notifications_modal.sent_at') }}:</strong>
                {{ formatLogDate(log.sentAt) }}
              </span>
              <span
                v-if="log.templateName"
                class="log-badge log-badge-template"
              >
                {{ log.templateName }}
              </span>
            </div>

            <div class="log-card-subtitle">
              <strong>{{ $t('notifications_modal.from') }}:</strong>
              <span>{{ formatSender(log.sender) }}</span>
            </div>
          </div>

          <div class="log-section">
            <div class="log-section-label">
              {{ $t('notifications_modal.to') }}
            </div>
            <div class="recipient-list">
              <span
                v-for="recipient in formatRecipientsList(log.recipients)"
                :key="recipient.email"
                class="recipient-chip"
                :title="`${recipient.name} <${recipient.email}>`"
              >
                {{ recipient.name || recipient.email }}
              </span>
            </div>
          </div>

          <div v-if="log.message || log.note" class="log-message-box">
            <div class="log-section-label">
              {{ $t('notifications_modal.notification_message') }}
            </div>
            <div
              class="notification-log-note content"
              v-html="formatLogMessage(log)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'notification-log-tab',
  data() {
    return {
      searchQuery: '',
    };
  },
  props: {
    notificationLogs: {
      type: [Array, Object],
      default: () => [],
    },
    isLoadingNotificationLog: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    normalizedLogs() {
      if (Array.isArray(this.notificationLogs)) {
        return this.notificationLogs;
      }
      if (Array.isArray(this.notificationLogs?.logs)) {
        return this.notificationLogs.logs;
      }
      if (Array.isArray(this.notificationLogs?.Items)) {
        return this.notificationLogs.Items;
      }
      return [];
    },
    filteredLogs() {
      const query = (this.searchQuery || '').toLowerCase().trim();
      if (!query) {
        return this.normalizedLogs;
      }

      return this.normalizedLogs.filter((log) =>
        this.buildSearchText(log).includes(query)
      );
    },
  },
  methods: {
    buildSearchText(log) {
      const recipients = this.formatRecipientsList(log.recipients).join(' ');
      const message = this.stripHtml(log.message || log.note || '');

      return [
        this.formatLogTitle(log),
        this.formatSender(log.sender),
        recipients,
        message,
        log.templateName || '',
        this.formatLogDate(log.sentAt),
      ]
        .join(' ')
        .toLowerCase();
    },
    stripHtml(value = '') {
      return String(value).replace(/<[^>]+>/g, ' ');
    },
    formatLogTitle(log) {
      return (
        log.subject ||
        log.templateName ||
        this.$t('notifications_modal.notification_log')
      );
    },
    formatSender(sender) {
      if (!sender) return '-';
      return sender.fullname && sender._id
        ? `${sender.fullname} <${sender._id}>`
        : sender.fullname || sender._id || '-';
    },
    formatRecipientsList(recipientIds = []) {
      if (!Array.isArray(recipientIds) || recipientIds.length === 0) {
        return [{ name: '-', email: '-' }];
      }

      return recipientIds.map((id) => {
        const user = this.users.find((u) => u._id === id);
        if (user && user.fullname) {
          return { name: user.fullname, email: id };
        }
        return { name: id, email: id };
      });
    },
    formatLogDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString();
    },
    formatLogMessage(log) {
      const raw = log.message || log.note || '';
      const text = String(raw);
      const hasHtml = /<[^>]+>/.test(text);
      if (hasHtml) return text;
      return text.replace(/\n/g, '<br>');
    },
  },
};
</script>

<style scoped>
.tab-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 0 4px;
}

.log-search {
  padding: 0 4px;
}

.log-toolbar-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #363636;
}

.log-toolbar-hint {
  font-size: 0.82rem;
  color: #7a7a7a;
}

.empty-state {
  text-align: center;
  color: #7a7a7a;
  padding: 20px;
  background: linear-gradient(180deg, #fafafa 0%, #f6f7fb 100%);
  border-radius: 12px;
  border: 1px solid #ececec;
}

.notification-log-box {
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  background: #fff;
  max-height: 520px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(10, 10, 10, 0.06);
}

.notification-log-items {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
}

.notification-log-item {
  padding: 16px 18px;
  border: 1px solid #eceff4;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fdf9ff 100%);
  border-left: 4px solid #d8b7e8;
  transition: box-shadow 160ms ease, transform 160ms ease;
}

.notification-log-item:hover {
  box-shadow: 0 10px 22px rgba(100, 24, 141, 0.12);
  transform: translateY(-1px);
}

.log-card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.log-card-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  justify-content: space-between;
}

.log-card-title {
  font-size: 1rem;
  font-weight: 800;
  color: #1f1f1f;
  line-height: 1.35;
}

.log-card-subtitle {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.9rem;
  color: #5e4674;
}

.log-meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f3e9f9;
  color: #5b2a76;
  font-size: 0.85rem;
}

.log-meta-pill-sent {
  margin-left: auto;
}

.log-section {
  margin-top: 14px;
}

.log-section-label {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6a4a84;
  margin-bottom: 8px;
}

.recipient-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.recipient-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  background: #f8f1fc;
  border: 1px solid #e5d1f0;
  color: #4a2e63;
  font-size: 0.85rem;
  cursor: default;
  white-space: nowrap;
}

.log-message-box {
  margin-top: 14px;
  padding: 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #ececec;
}

.notification-log-note {
  margin-top: 10px;
  white-space: pre-wrap;
}

.log-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.log-badge-template {
  background: #f3e9f9;
  color: #64188d;
}
</style>
